import React, { useRef, useState } from 'react';
import { Search, Loader2, Upload, ImageIcon, X, Package, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { currencyMeta } from '../lib/currency';

const API = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? `${API}/api/sourcing/img?u=${encodeURIComponent(u)}` : '');

const fmtCur = (item, currency) => {
  const v = item.prices?.[currency];
  if (v === undefined || v === null) return null;
  const m = currencyMeta(currency);
  return `${m.symbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: m.decimals, maximumFractionDigits: m.decimals })}`;
};

export function ResultsGrid({ results, currency, onSelect, loadingId, onPage }) {
  if (!results) return null;
  if (results.items.length === 0) return <p className="text-sm text-gray-500 text-center py-6" data-testid="search-empty">No products found. Try different words or another photo.</p>;
  return (
    <div className="space-y-3" data-testid="search-results">
      <p className="text-xs text-gray-500">{results.total.toLocaleString()} factory listings found{results.query ? ` for "${results.query}"` : ''} · page {results.page}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {results.items.map((it) => (
          <button key={it.item_id} type="button" onClick={() => onSelect(it)} disabled={!!loadingId}
            className="group bg-white border border-gray-200 rounded-xl overflow-hidden text-left hover:border-orange-400 hover:shadow-lg transition-all disabled:opacity-60"
            data-testid={`result-card-${it.item_id}`}>
            <div className="aspect-square bg-gray-100 relative overflow-hidden">
              {it.image ? <img src={imgSrc(it.image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                : <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-gray-300" /></div>}
              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-orange-600 text-white text-[10px] font-bold">1688</span>
              {loadingId === it.item_id && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>}
            </div>
            <div className="p-2.5">
              <p className="text-xs text-gray-800 leading-snug line-clamp-2 h-8" data-testid="result-title">{it.title}</p>
              <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
                <span className="text-orange-600 font-extrabold text-base" data-testid="result-price-rmb">¥{it.price_rmb.toFixed(2)}</span>
                {fmtCur(it, currency) && <span className="text-emerald-700 text-xs font-semibold" data-testid="result-price-fx">≈ {fmtCur(it, currency)}</span>}
              </div>
              <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                <span data-testid="result-moq">MOQ {it.moq}</span>
                {it.sales ? <span>{Number(it.sales).toLocaleString()} sold</span> : null}
              </div>
              <div className="mt-2 flex items-center justify-center gap-1 text-[11px] font-bold text-orange-600 bg-orange-50 rounded-lg py-1 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <ShoppingCart className="w-3 h-3" /> View options & Agent Buy
              </div>
            </div>
          </button>
        ))}
      </div>
      {onPage && (
        <div className="flex justify-center gap-2 pt-1">
          <Button variant="outline" size="sm" disabled={results.page <= 1 || !!loadingId} onClick={() => onPage(results.page - 1)} data-testid="search-prev"><ChevronLeft className="w-4 h-4" /> Prev</Button>
          <Button variant="outline" size="sm" disabled={results.items.length < 20 || !!loadingId} onClick={() => onPage(results.page + 1)} data-testid="search-next">Next <ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

export function KeywordSearch({ currency, onSelect, loadingId }) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const run = async (page = 1, e) => {
    if (e) e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sourcing/search?q=${encodeURIComponent(q.trim())}&page=${page}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Search failed');
      setResults(data);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="keyword-search">
      <form onSubmit={(e) => run(1, e)} className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Type what you need, e.g. "LED Flood Light", "solar inverter", "ladies handbag"...'
            className="w-full pl-11 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-sm"
            required data-testid="keyword-input" />
        </div>
        <Button type="submit" disabled={loading} className="min-w-[160px] bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl shadow-md" data-testid="keyword-search-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}{loading ? 'Searching 1688...' : 'Search Factories'}
        </Button>
      </form>
      {loading && !results && <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100 text-blue-700 font-medium flex items-center justify-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /> Searching 1688 factory listings...</div>}
      <ResultsGrid results={results} currency={currency} onSelect={onSelect} loadingId={loadingId} onPage={(p) => run(p)} />
    </div>
  );
}

export function PhotoSearch({ currency, onSelect, loadingId }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image file (JPG, PNG, WEBP)'); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error('Image must be under 6 MB'); return; }
    setFile(f); setPreview(URL.createObjectURL(f)); setResults(null);
  };

  const run = async (page = 1) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('page', String(page));
      const res = await fetch(`${API}/api/sourcing/search-by-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Image search failed');
      setResults(data);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="photo-search">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        onClick={() => !file && inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5 transition-colors cursor-pointer ${drag ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50 hover:border-orange-400'}`}
        data-testid="photo-dropzone">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="photo-file-input" />
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Your product" className="w-32 h-32 object-cover rounded-xl border border-gray-200 shadow" data-testid="photo-preview" />
            <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(''); setResults(null); }} className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:text-red-600" data-testid="photo-clear"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <div className="w-32 h-32 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0"><ImageIcon className="w-12 h-12 text-orange-300" /></div>
        )}
        <div className="flex-1 text-center sm:text-left">
          <p className="font-bold text-gray-900">{file ? file.name : 'Drag & drop a product photo here'}</p>
          <p className="text-sm text-gray-500 mt-1">{file ? `${(file.size / 1024).toFixed(0)} KB · ready to search` : 'or click to browse — JPG, PNG or WEBP up to 6 MB. We find the same or similar products from 1688 factories.'}</p>
          <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
            <Button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} variant="outline" className="rounded-xl" data-testid="photo-browse-btn"><Upload className="w-4 h-4 mr-2" /> {file ? 'Choose another' : 'Browse photo'}</Button>
            <Button type="button" onClick={(e) => { e.stopPropagation(); run(1); }} disabled={!file || loading} className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl" data-testid="photo-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}{loading ? 'Matching products...' : 'Find matching factories'}
            </Button>
          </div>
        </div>
      </div>
      <ResultsGrid results={results} currency={currency} onSelect={onSelect} loadingId={loadingId} onPage={(p) => run(p)} />
    </div>
  );
}
